import { useTranslation } from 'react-i18next'
import { TeamMembersTable } from './team-members.style'
import { Cancel, CheckCircle } from '@mui/icons-material'
import { useAuth } from '../../../hooks/useAuth'
import { Button, Tooltip } from '@mui/material'
import { InviteMember } from '../../../features/team-member/InviteMember'
import { useState } from 'react'
import { Info } from 'react-feather'

export interface TeamMember {
  name: string
  email: string
  title: string
  picture: string
  timezone: string
  calendarConnected: boolean
  calendarScope?: {
    canCheckAvailability: boolean
    canInsertEvents: boolean
  }
  registered: boolean
  _id: string
}

export interface TeamMemberDTO {
  title: string
  email: string
  name: string
}

export interface TeamMemberAssessmentStatus {
  _id: string
  name: string
  email: string
  title: string
  completed: boolean
}

export interface TeamMembersParams {
  includeInvited?: string
}

const getStatusSubClassName = (member: TeamMember) => {
  if (!member.registered) {
    return ''
  }
  if (
    member.calendarScope?.canCheckAvailability &&
    member.calendarScope?.canInsertEvents
  ) {
    return 'connected'
  }
  if (
    member.calendarScope?.canCheckAvailability ||
    member.calendarScope?.canInsertEvents
  ) {
    return 'partiallyConnected'
  }
  return 'notConnected'
}

const getStatusText = (member: TeamMember) => {
  if (!member.registered) {
    return 'notRegistered'
  }
  if (
    member.calendarScope?.canCheckAvailability &&
    member.calendarScope?.canInsertEvents
  ) {
    return 'calendarConnected'
  }
  if (
    member.calendarScope?.canCheckAvailability ||
    member.calendarScope?.canInsertEvents
  ) {
    return 'partiallyConnected'
  }

  return 'notConnected'
}

const getIcon = (member: TeamMember, t: any) => {
  if (!member.registered) {
    return <></>
  }

  if (
    member.calendarScope?.canCheckAvailability &&
    member.calendarScope?.canInsertEvents
  ) {
    return <CheckCircle />
  }
  if (
    member.calendarScope?.canCheckAvailability ||
    member.calendarScope?.canInsertEvents
  ) {
    const title = member.calendarScope?.canCheckAvailability
      ? t('canOnlyCheckAvailability')
      : t('canOnlyInsertEvents')
    return (
      <Tooltip title={title}>
        <Info />
      </Tooltip>
    )
  }

  return <Cancel />
}

export function TeamMembers({ members }: { members: TeamMember[] }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const isManager = user?.user.isManager
  const nonRegisteredStyle = { color: '#44444444' }
  const [inviteMember, setInviteMember] = useState<any>(null)
  return (
    <>
      <TeamMembersTable>
        <thead>
          <tr>
            <th>#</th>
            <th> {t('name')} </th>
            <th> {t('title')} </th>
            <th> {t('email')} </th>
            <th> {t('timezone')} </th>
            {isManager && <th> {t('status')} </th>}
          </tr>
        </thead>
        <tbody>
          {members.map((member, index) => (
            <tr key={`member_${index}`}>
              <td style={member.registered ? {} : nonRegisteredStyle}>
                {index + 1}
              </td>
              <td>
                <div>
                  <p style={member.registered ? {} : nonRegisteredStyle}>
                    {member.name}
                  </p>
                </div>
              </td>
              <td style={member.registered ? {} : nonRegisteredStyle}>
                {member.title}
              </td>
              <td style={member.registered ? {} : nonRegisteredStyle}>
                {member.email}
              </td>
              <td style={member.registered ? {} : nonRegisteredStyle}>
                {member.timezone}
              </td>
              {isManager && (
                <td style={member.registered ? {} : nonRegisteredStyle}>
                  <span
                    className={`calendarStatus ${getStatusSubClassName(
                      member,
                    )}`}
                  >
                    {getIcon(member, t)}
                    {t(getStatusText(member))}
                    {!member.registered && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setInviteMember(member)}
                      >
                        {t('resendInvitation')}
                      </Button>
                    )}
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </TeamMembersTable>
      {inviteMember && (
        <InviteMember
          onClose={() => setInviteMember(null)}
          member={inviteMember}
        />
      )}
    </>
  )
}
